import { type ICustomFieldRepository } from '@webapp/core/business-logic/interfaces/ICustomFieldRepository'
import { type CustomFieldApiModel } from '@webapp/core/data-access/api-models/CustomField'
import { mapCustomField } from '@webapp/core/data-access/mappers/mapCustomField'
import { type CustomFieldConstructorData } from '@webapp/core/entities/internal/CustomField'
import { ApiClient } from '@webapp/core/services/ApiClient'
import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type CreateCustomFieldPayload } from './types/CreateCustomFieldPayload'
import { type GetCustomFieldsResponse } from './types/GetCustomFieldsResponse'
import { type UpdateCustomFieldPayload } from './types/UpdateCustomFieldPayload'

interface Dependency {
  httpClient: IHttpClient
}

const ENDPOINT = '/api/v3/custom-fields'

export class CustomFieldRepository implements ICustomFieldRepository {
  constructor(private readonly deps: Dependency) {}

  async getAPItemCustomFields(
    params: RepoFilterParams = {}
  ): Promise<RepoPaginatedResponse<CustomFieldConstructorData>> {
    try {
      const response = await this.deps.httpClient.get<GetCustomFieldsResponse>(
        `${ENDPOINT}/ap-items/`,
        { params }
      )

      const customFields = response.data.data
      const metadata = response.data.metadata

      return {
        data: customFields.map((cf) => mapCustomField(cf)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getSpendingCardCustomFields(
    params: RepoFilterParams = {}
  ): Promise<RepoPaginatedResponse<CustomFieldConstructorData>> {
    try {
      const response = await this.deps.httpClient.get<GetCustomFieldsResponse>(
        `${ENDPOINT}/pay-transactions/`,
        { params }
      )

      const customFields = response.data.data
      const metadata = response.data.metadata

      return {
        data: customFields.map((cf) => mapCustomField(cf)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getOrderItemCustomFields(
    params: RepoFilterParams = {}
  ): Promise<RepoPaginatedResponse<CustomFieldConstructorData>> {
    try {
      const response = await this.deps.httpClient.get<GetCustomFieldsResponse>(
        `${ENDPOINT}/order-items/`,
        { params: formatRepoFiltersToApiFilters(params) }
      )

      const customFields = response.data.data
      const metadata = response.data.metadata

      return {
        data: customFields.map((cf) => mapCustomField(cf)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getPurchaseOrderCustomFields(
    params: RepoFilterParams = {}
  ): Promise<RepoPaginatedResponse<CustomFieldConstructorData>> {
    try {
      const response = await this.deps.httpClient.get<GetCustomFieldsResponse>(
        `${ENDPOINT}/purchase-orders/`,
        { params: formatRepoFiltersToApiFilters(params) }
      )

      const customFields = response.data.data
      const metadata = response.data.metadata

      return {
        data: customFields.map((cf) => mapCustomField(cf)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async createCustomField(
    payload: CreateCustomFieldPayload
  ): Promise<{ id: number }> {
    try {
      const response = await this.deps.httpClient.post<{
        data: CustomFieldApiModel
      }>(`${ENDPOINT}/`, payload)

      return { id: response.data.data.id }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async updateCustomField(
    id: number,
    payload: UpdateCustomFieldPayload
  ): Promise<void> {
    try {
      await this.deps.httpClient.put(`${ENDPOINT}/${id}/`, payload)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async disableCustomField(id: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(`${ENDPOINT}/${id}/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async activateCustomField(id: number): Promise<void> {
    try {
      await this.deps.httpClient.put(`${ENDPOINT}/${id}/activate/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}

export const customFieldRepository = new CustomFieldRepository({
  httpClient: ApiClient,
})
